import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { DashboardAlerts, DashboardAlert, AlertSeverity } from '../../types/dashboard.types';

interface DashboardAlertsListProps {
  alerts: DashboardAlerts;
}

/**
 * Get icon component based on alert severity.
 */
function getSeverityIcon(severity: AlertSeverity) {
  switch (severity) {
    case 'critical':
      return ExclamationCircleIcon;
    case 'warning':
      return ExclamationTriangleIcon;
    case 'info':
    default:
      return InformationCircleIcon;
  }
}

/**
 * Get color classes based on alert severity.
 */
function getSeverityColors(severity: AlertSeverity): {
  bg: string;
  text: string;
  icon: string;
  border: string;
} {
  switch (severity) {
    case 'critical':
      return {
        bg: 'bg-red-50',
        text: 'text-red-800',
        icon: 'text-red-500',
        border: 'border-red-200',
      };
    case 'warning':
      return {
        bg: 'bg-amber-50',
        text: 'text-amber-800',
        icon: 'text-amber-500',
        border: 'border-amber-200',
      };
    case 'info':
    default:
      return {
        bg: 'bg-blue-50',
        text: 'text-blue-800',
        icon: 'text-blue-500',
        border: 'border-blue-200',
      };
  }
}

/**
 * Build the filter URL with source=dashboard appended.
 */
function buildFilterUrl(filterUrl: string): string {
  const separator = filterUrl.includes('?') ? '&' : '?';
  return `${filterUrl}${separator}source=dashboard`;
}

/**
 * Single alert item component.
 * Uses translation keys for i18n support.
 */
const AlertItem: React.FC<{ alert: DashboardAlert }> = ({ alert }) => {
  const { t } = useTranslation();
  const colors = getSeverityColors(alert.severity);
  const Icon = getSeverityIcon(alert.severity);

  // Translate the title using the violation_type key
  const title = t(`dashboard:alerts.types.${alert.violation_type}`);

  // Format the description based on period
  const descriptionKey = alert.period_days === 1 ? 'callCountToday' : 'callCount';
  const description = t(`dashboard:alerts.${descriptionKey}`, {
    count: alert.count,
    period_days: alert.period_days,
  });

  return (
    <Link
      to={buildFilterUrl(alert.filter_url)}
      className={`block ${colors.bg} rounded-lg p-4 border ${colors.border} hover:opacity-90 transition-opacity`}
    >
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <Icon className={`h-5 w-5 ${colors.icon}`} aria-hidden="true" />
        </div>
        <div className="ml-3 flex-1">
          <h4 className={`text-sm font-medium ${colors.text}`}>
            {title}
          </h4>
          <p className="mt-1 text-sm text-gray-600">
            {description}
          </p>
        </div>
        <div className="ml-3 flex-shrink-0">
          <ArrowRightIcon className={`h-5 w-5 ${colors.icon}`} aria-hidden="true" />
        </div>
      </div>
    </Link>
  );
};

/**
 * Empty state when there are no alerts.
 */
const EmptyAlerts: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="text-center py-8">
      <CheckCircleIcon className="mx-auto h-12 w-12 text-green-400" />
      <h3 className="mt-2 text-sm font-medium text-gray-900">
        {t('dashboard:alerts.noIssues')}
      </h3>
      <p className="mt-1 text-sm text-gray-500">
        {t('dashboard:alerts.allClear')}
      </p>
    </div>
  );
};

/**
 * Alerts list showing compliance violations requiring attention.
 * Sorted by severity (critical first) and links to filtered analyses.
 */
const DashboardAlertsList: React.FC<DashboardAlertsListProps> = ({ alerts }) => {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          {t('dashboard:alerts.title')}
        </h3>
        {(alerts.total_critical > 0 || alerts.total_warning > 0) && (
          <div className="flex space-x-2">
            {alerts.total_critical > 0 && (
              <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                {alerts.total_critical} {t('dashboard:alerts.critical')}
              </span>
            )}
            {alerts.total_warning > 0 && (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                {alerts.total_warning} {t('dashboard:alerts.warning')}
              </span>
            )}
          </div>
        )}
      </div>

      {alerts.alerts.length === 0 ? (
        <EmptyAlerts />
      ) : (
        <div className="space-y-3">
          {alerts.alerts.map((alert, index) => (
            <AlertItem key={`${alert.type}-${index}`} alert={alert} />
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardAlertsList;
